(() => {
  /**
   * Initializes the MutationObserver for the swiper element with the given ID.
   *
   * @param {string} swiperId - The ID of the swiper element to observe.
   */
  const initSwiperObserver = (swiperId) => {
    const targetNode = document.body; // Can be a more specific element if applicable
    const config = { childList: true, subtree: true }; // Configuration to observe child additions

    /**
     * Callback function to handle mutations observed.
     *
     * @param {MutationRecord[]} mutationsList - List of mutations that occurred.
     */
    const callback = (mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'childList') {
          const swiperElement = document.getElementById(swiperId);
          if (swiperElement && swiperElement.swiper && !isSwiperLoaded(swiperElement)) {
            applyListenerToParentSwiper(swiperElement); // Initialize the swiper
            break; // Stop the loop after initializing the swiper
          }
        }
      }
    };

    const observer = new MutationObserver(callback);
    observer.observe(targetNode, config); // Start observing the target node

    // Call the function to initialize immediately if the swiper is already loaded
    const swiperElement = document.getElementById(swiperId);
    if (swiperElement && swiperElement.swiper && !isSwiperLoaded(swiperElement)) {
      applyListenerToParentSwiper(swiperElement); // Initialize the swiper
    }
  };

  /**
   * Checks if the swiper instance has already been loaded.
   *
   * @param {Element} swiperElement - The swiper element to check.
   * @returns {boolean} - Returns true if the swiper is loaded, false otherwise.
   */
  const isSwiperLoaded = (swiperElement) => {
    return swiperElement.dataset.isLoaded === 'true';
  };

  /**
   * Applies the event listener to the parent swiper instance.
   *
   * @param {Element} swiperElement - The swiper element to apply the listener to.
   */
  const applyListenerToParentSwiper = (swiperElement) => {
    const swiper = swiperElement.swiper;
    if (!swiper) return;

    // Set up the slide change event listener
    swiper.on('slideChange', () => {
      const currentIndex = swiper.activeIndex + 1; // Get the current active index
      handleSliderNavigation(swiperElement, currentIndex); // Handle navigation
    });

    swiperElement.setAttribute('data-is-loaded', 'true'); // Mark the swiper as loaded
  };

  /**
   * Handles the navigation of the swiper slider based on the given index.
   *
   * @param {Event} event - The event object that triggered the navigation.
   * @param {string|number} index - The navigation direction ('next', 'prev') or a specific slide index.
   */
  const handleSliderNavigation = (event, index) => {
    const component = getComponentFromEvent(event);
    const slider = document.getElementById(component.dataset.uniqueSwiperSelector);
    const totalSlides = slider.swiper.slides.length;
    const currentIndex = slider.swiper.activeIndex + 1; // Get the current slide index
    const nextIndex = getNextIndex(index, currentIndex); // Get the next index

    if (nextIndex === null) return; // If nextIndex is null, exit the function

    console.log('----------------------------------');
    console.log(nextIndex + ' -- ' + totalSlides);

    updateNavigationButtons(component, nextIndex, totalSlides); // Update navigation buttons (prev/next)
    updateDots(component, nextIndex); // Update the active state of dots
    updatePagination(component, nextIndex); // Update the current page display
    updateSwiper(slider, index, nextIndex); // Update the swiper instance based on the index
  };

  /**
   * Retrieves the component element from the event.
   *
   * @param {Event} event - The event object.
   * @returns {Element} - The component element containing the swiper.
   */
  const getComponentFromEvent = (event) => {
    return event.swiper
      ? event.closest('section').querySelector('[data-unique-swiper-selector]')
      : event.target.closest('[data-unique-swiper-selector]');
  };

  /**
   * Calculates the next index based on the current index and the navigation direction.
   *
   * @param {string|number} index - The navigation direction ('next', 'prev') or a specific slide index.
   * @param {number} currentIndex - The current slide index.
   * @returns {number|null} - The next index to navigate to, or null if invalid.
   */
  const getNextIndex = (index, currentIndex) => {
    if (index === 'next') return currentIndex + 1;
    if (index === 'prev') return currentIndex - 1;
    return parseInt(index); // Return the specific index if provided
  };

  /**
   * Updates the state of navigation buttons based on the current index.
   *
   * @param {Element} component - The component element containing the buttons.
   * @param {number} nextIndex - The next index to set.
   * @param {number} totalSlides - The total number of slides.
   */
  const updateNavigationButtons = (component, nextIndex, totalSlides) => {
    const prevButton = component.querySelector('.navigation_arrows .--arrow_prev');
    const nextButton = component.querySelector('.navigation_arrows .--arrow_next');

    prevButton.disabled = nextIndex === 1; // Disable prev button if at first slide
    nextButton.disabled = nextIndex === totalSlides; // Disable next button if at last slide
  };

  /**
   * Updates the active state of dots indicating the current slide.
   *
   * @param {Element} component - The component element containing the dots.
   * @param {number} nextIndex - The index of the next slide to activate the dot for.
   */
  const updateDots = (component, nextIndex) => {
    const dots = component.querySelectorAll('.control__dot');
    dots.forEach((dot) => {
      const dotIndex = parseInt(dot.dataset.dotIndex); // Get the index of the dot
      dot.classList.toggle('active', dotIndex === nextIndex); // Toggle active class
    });
  };

  /**
   * Updates the current page display to reflect the currently active slide.
   *
   * @param {Element} component - The component element containing the pagination.
   * @param {number} nextIndex - The index of the next slide.
   */
  const updatePagination = (component, nextIndex) => {
    const currentPage = component.querySelector('.current_page');
    currentPage.textContent = nextIndex; // Set the current page text
  };

  /**
   * Updates the swiper instance based on the navigation direction.
   *
   * @param {Element} slider - The slider element containing the swiper instance.
   * @param {string|number} index - The navigation direction ('next', 'prev') or a specific slide index.
   * @param {number} nextIndex - The index of the next slide.
   */
  const updateSwiper = (slider, index, nextIndex) => {
    if (slider) {
      if (index === 'next') {
        slider.swiper.slideNext(); // Move to the next slide
      } else if (index === 'prev') {
        slider.swiper.slidePrev(); // Move to the previous slide
      } else {
        slider.swiper.slideTo(nextIndex - 1); // Move to a specific slide (0-based index)
      }
    }
  };

  const desktopNavigationTrigger = (event, type) => {
    if (event && type) {
      const section = event.target.closest('section');
      const controls = section.querySelector('.navigation_arrows');
      let prev = controls.querySelector('.--arrow_prev');
      let next = controls.querySelector('.--arrow_next');
      switch (type) {
        case 'trigger-prev':
          prev.click();
        break;
        case 'trigger-next':
          next.click();
        break;
        default:
      }
      // if (prev.attributes.disabled) {
      //   event.target.attributes('disabled', true);
      // } else {
      //   event.target.removeAttribute('disabled');
      // }
      // if (next.attributes.disabled) {
      //   event.target.attributes('disabled', true);
      // } else {
      //   event.target.removeAttribute('disabled');
      // }
    }
  };

  // Expose the function to be called externally
  window.initSwiperObserver = initSwiperObserver;
  window.handleSliderNavigation = handleSliderNavigation;
  window.desktopNavigationTrigger = desktopNavigationTrigger;
})();
